'use client';

import { type UpdateSession, useSession } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import { createContext, type PropsWithChildren, useContext, useEffect, useMemo } from 'react';

import { LOGIN_PATH } from '@module/mdrt-org/shared/utils/constants/local-paths';
import { ADB2CUserGroup } from '@shared/misc/next-auth/enum';

type UserContextValue = {
  accessToken: string | undefined;
  authenticatedPersonId: number | undefined;
  groups: string[] | undefined;
  hasCompanyAdminRole: boolean | undefined;
  hasStaffRole: boolean | undefined;
  isAuthenticated: boolean;
  personId: number | undefined;
  personIdBeforeImpersonation: number | undefined;
  status: 'authenticated' | 'loading' | 'unauthenticated';
  updateSession: UpdateSession;
};

const UserContext = createContext<undefined | UserContextValue>(undefined);

export const UserContextProvider = ({ children }: PropsWithChildren) => {
  const { data, status, update: updateSession } = useSession();
  const router = useRouter();

  const groups = data?.groups;
  const isAuthenticated = status === 'authenticated';
  const hasStaffRole = isAuthenticated && groups?.includes(ADB2CUserGroup.MDRTStaff);
  const hasCompanyAdminRole = isAuthenticated && groups?.includes(ADB2CUserGroup.CompanyAdmin);

  const value: UserContextValue = useMemo(
    () => ({
      accessToken: data?.accessToken,
      // The actual logged-in user the access token represents. When impersonating,
      // `personIdBeforeImpersonation` holds it; otherwise it is simply `personId`.
      authenticatedPersonId: data?.personIdBeforeImpersonation ?? data?.personId,
      groups,
      hasCompanyAdminRole,
      hasStaffRole,
      isAuthenticated,
      personId: data?.personId,
      personIdBeforeImpersonation: data?.personIdBeforeImpersonation,
      status,
      updateSession,
    }),
    [
      data?.personIdBeforeImpersonation,
      data?.accessToken,
      data?.personId,
      groups,
      hasCompanyAdminRole,
      hasStaffRole,
      status,
      updateSession,
      isAuthenticated,
    ]
  );

  useEffect(() => {
    if (data?.error) {
      router.push(LOGIN_PATH);
    }
  }, [data?.error, router]);

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUserContext = () => {
  const value = useContext(UserContext);

  if (!value) {
    throw new Error(`${useUserContext.name} must be used inside of ${UserContextProvider.name}`);
  }

  return value;
};
